import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { Zap, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./i18n";
import { api } from "./services/api";
import { SpotlightCard } from "./components/ui/SpotlightCard";
import { AlphaFeed } from "./components/feed/AlphaFeed";
import { DeepDiveDrawer } from "./components/drawer/DeepDiveDrawer";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { PlatformOverview } from "./components/landing/PlatformOverview";
import { Intelligence } from "./types/intelligence";
import { cn } from "./utils/cn";

function App() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedItem, setSelectedItem] = useState<Intelligence | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.getStats(),
    refetchInterval: 60000,
  });

  const { data: miningStatus } = useQuery({
    queryKey: ["miningStatus"],
    queryFn: () => api.getMiningStatus(),
    refetchInterval: 30000,
  });

  const stats = statsData?.data;
  const mining = miningStatus?.data;

  const handleItemClick = (item: Intelligence) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const formatScore = (score: number): string => score.toFixed(1);

  const getScoreColor = (score: number): string => {
    if (score >= 70) return "text-emerald-600";
    if (score >= 50) return "text-blue-600";
    if (score >= 30) return "text-amber-600";
    return "text-slate-500";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-lg font-bold text-slate-800">Menlo</h1>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Activity className="w-4 h-4 text-emerald-500" />
                <span>{t("common.live")}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Title Section */}
        <div className="text-center mb-4">
          <h2
            className={cn(
              "text-xl md:text-2xl font-bold",
              "bg-gradient-to-r from-[#1E40AF] via-[#3B82F6] to-[#1E40AF]",
              "bg-clip-text text-transparent",
            )}
          >
            {t("hero.title")}
          </h2>
          <p className="text-sm md:text-base text-slate-500 mt-1.5 max-w-2xl mx-auto">
            {t("hero.subtitle")}
          </p>
        </div>

        {/* Platform Overview - Consolidated Features & Scoring */}
        <div className="mb-4">
          <PlatformOverview defaultExpanded={true} />
        </div>

        {/* Stats Section */}
        <section className="mb-2">
          <h2 className="text-lg font-medium text-slate-800 mb-4">
            {t("stats.title")}
          </h2>
          {statsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 bg-white border border-slate-200 rounded-xl animate-pulse"
                />
              ))}
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SpotlightCard
                className="p-4"
                spotlightColor="rgba(59, 130, 246, 0.1)"
              >
                <p className="text-sm text-slate-500">
                  {t("stats.totalIntelligence")}
                </p>
                <p className="text-2xl font-semibold text-slate-800 mt-1 num">
                  {stats.total_count}
                </p>
              </SpotlightCard>
              <SpotlightCard
                className="p-4"
                spotlightColor="rgba(16, 185, 129, 0.1)"
              >
                <p className="text-sm text-slate-500">
                  {t("stats.avgCavScore")}
                </p>
                <p
                  className={cn(
                    "text-2xl font-semibold mt-1 num",
                    getScoreColor(stats.avg_cav_score),
                  )}
                >
                  {formatScore(stats.avg_cav_score)}
                </p>
              </SpotlightCard>
              <SpotlightCard
                className="p-4"
                spotlightColor="rgba(245, 158, 11, 0.1)"
              >
                <p className="text-sm text-slate-500">
                  {t("stats.maxCavScore")}
                </p>
                <p
                  className={cn(
                    "text-2xl font-semibold mt-1 num",
                    getScoreColor(stats.max_cav_score),
                  )}
                >
                  {formatScore(stats.max_cav_score)}
                </p>
              </SpotlightCard>
              <SpotlightCard
                className="p-4"
                spotlightColor="rgba(139, 92, 246, 0.1)"
              >
                <p className="text-sm text-slate-500">
                  {t("stats.highScoreItems")}
                </p>
                <p className="text-2xl font-semibold text-emerald-600 mt-1 num">
                  {stats.high_score_count}
                </p>
              </SpotlightCard>
            </div>
          ) : null}
        </section>

        {/* Mining Status */}
        {mining && (
          <section className="mb-2">
            <SpotlightCard className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full",
                      mining.status === "running"
                        ? "bg-blue-500 animate-pulse"
                        : mining.status === "completed"
                          ? "bg-emerald-500"
                          : "bg-red-500",
                    )}
                  />
                  <span className="text-sm text-slate-600">
                    {t("mining.lastJob")}:{" "}
                    <span className="font-medium">
                      {t(`mining.${mining.status}`)}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>
                    {t("mining.papers")}: {mining.papers_processed}
                  </span>
                  <span>
                    {t("mining.repos")}: {mining.repos_found}
                  </span>
                  <span>
                    {t("mining.analyzed")}: {mining.ai_analyzed}
                  </span>
                </div>
              </div>
            </SpotlightCard>
          </section>
        )}

        {/* Alpha Feed */}
        <section id="alpha-feed" ref={feedRef}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-slate-800">
              {t("feed.title")}
            </h2>
            <button
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ["intelligence"] })
              }
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            >
              {t("common.refresh")}
            </button>
          </div>
          <AlphaFeed onItemClick={handleItemClick} />
        </section>
      </main>

      {/* Deep Dive Drawer */}
      <DeepDiveDrawer
        item={selectedItem}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-slate-500">
            {t("common.poweredBy")}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
